import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { IconX } from "@tabler/icons-react";
import { cn } from "../../../../lib/utils";
import { DesktopDialogOverlay } from "../../../../components/app/DesktopDialogOverlay";

/** The round close button of the settings modal and of a `SettingsDialog`. */
export function SettingsCloseButton({
  onClose,
  size,
  className,
}: {
  onClose: () => void;
  /** `md` on the modal, `sm` on a dialog. */
  size: "sm" | "md";
  className: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-label={t("Close")}
      onClick={onClose}
      className={cn(
        "flex shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-ds-slate-100 text-ds-slate-950 hover:bg-ds-slate-200",
        size === "md" ? "p-1.5 [&_svg]:size-4.5" : "p-1.25 [&_svg]:size-4",
        className
      )}
    >
      <IconX stroke={1.5} />
    </button>
  );
}

/**
 * A small titled dialog in the settings style (the partners list, the privacy
 * policy, the room welcome). Never over the settings modal itself: there an
 * edit or a confirmation happens in the row or on a page of the modal.
 */
export function SettingsDialog({
  title,
  onClose,
  width,
  children,
}: {
  title: string;
  onClose: () => void;
  /** `sm` for a one-field form, `md` and `lg` for longer confirmations. */
  width: "sm" | "md" | "lg";
  children: ReactNode;
}): React.JSX.Element {
  return (
    <DesktopDialogOverlay
      label={title}
      onClose={onClose}
      cardClassName={cn(
        "flex w-full flex-col gap-3 rounded-2xl bg-ds-white p-6 shadow-desktop-modal",
        width === "sm"
          ? "max-w-105"
          : width === "md"
            ? "max-w-115"
            : "max-w-120"
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-heading-xs font-semibold text-ds-slate-950">
          {title}
        </h2>
        <SettingsCloseButton onClose={onClose} size="sm" className={null} />
      </div>
      {children}
    </DesktopDialogOverlay>
  );
}
